// src/components/MapboxFieldEditor.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import mapboxgl from "mapbox-gl";
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import { addTerrainLayer, SATELLITE_STYLE } from "../utils/mapTerrain.js";

import "mapbox-gl/dist/mapbox-gl.css";
import "@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css";

function parseMaybeJson(value) {
  if (!value) return null;
  if (typeof value === "object") return value;
  if (typeof value !== "string") return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function normalizeToEditableFeature(geometry) {
  const g = parseMaybeJson(geometry);
  if (!g) return null;

  if (g.type === "Feature" && g.geometry) {
    if (g.geometry.type === "Polygon") return g;
    if (g.geometry.type === "MultiPolygon") {
      return {
        type: "Feature",
        properties: {},
        geometry: {
          type: "Polygon",
          coordinates: g.geometry.coordinates?.[0] || [],
        },
      };
    }
    return null;
  }

  if (g.type === "Polygon") {
    return {
      type: "Feature",
      properties: {},
      geometry: g,
    };
  }

  if (g.type === "MultiPolygon") {
    return {
      type: "Feature",
      properties: {},
      geometry: {
        type: "Polygon",
        coordinates: g.coordinates?.[0] || [],
      },
    };
  }

  return null;
}

function getGeometryOnly(value) {
  const g = parseMaybeJson(value);
  if (!g) return null;
  if (g.type === "Feature" && g.geometry) return g.geometry;
  return g;
}

function computeBBox(geometry) {
  const g = getGeometryOnly(geometry);
  if (!g) return null;

  let coords = [];

  if (g.type === "Polygon") {
    coords = g.coordinates?.flat(1) || [];
  } else if (g.type === "MultiPolygon") {
    coords = g.coordinates?.flat(2) || [];
  } else {
    return null;
  }

  let minLng = Infinity;
  let minLat = Infinity;
  let maxLng = -Infinity;
  let maxLat = -Infinity;

  for (const p of coords) {
    const lng = Number(p?.[0]);
    const lat = Number(p?.[1]);
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) continue;

    minLng = Math.min(minLng, lng);
    minLat = Math.min(minLat, lat);
    maxLng = Math.max(maxLng, lng);
    maxLat = Math.max(maxLat, lat);
  }

  if (
    !Number.isFinite(minLng) ||
    !Number.isFinite(minLat) ||
    !Number.isFinite(maxLng) ||
    !Number.isFinite(maxLat)
  ) {
    return null;
  }

  return [minLng, minLat, maxLng, maxLat];
}

function ensureMapboxToken() {
  const token =
    mapboxgl.accessToken ||
    import.meta.env.VITE_MAPBOX_TOKEN ||
    import.meta.env.VITE_MAPBOX_ACCESS_TOKEN ||
    "";

  if (!mapboxgl.accessToken && token) {
    mapboxgl.accessToken = token;
  }

  return !!mapboxgl.accessToken;
}

export default function MapboxFieldEditor({
  geometry,
  height = 520,
  onChange,
  className = "",
}) {
  const mapRef = useRef(null);
  const mapNodeRef = useRef(null);
  const drawRef = useRef(null);

  const [ready, setReady] = useState(false);
  const [error, setError] = useState("");

  const initialFeature = useMemo(() => normalizeToEditableFeature(geometry), [geometry]);

  useEffect(() => {
    if (!mapNodeRef.current) return;

    if (!ensureMapboxToken()) {
      setError(
        "Не знайдено Mapbox token. Додай VITE_MAPBOX_TOKEN у .env або переконайся, що token вже ініціалізований."
      );
      return;
    }

    const map = new mapboxgl.Map({
      container: mapNodeRef.current,
      style: SATELLITE_STYLE,
      center: [31.13, 48.38],
      zoom: 5.5,
      attributionControl: false,
    });

    mapRef.current = map;

    const draw = new MapboxDraw({
      displayControlsDefault: false,
      defaultMode: "simple_select",
      controls: {
        polygon: true,
        trash: true,
      },
      styles: [
        {
          id: "gl-draw-polygon-fill-custom",
          type: "fill",
          filter: ["all", ["==", "$type", "Polygon"], ["!=", "mode", "static"]],
          paint: {
            "fill-color": "#22c55e",
            "fill-opacity": 0.18,
          },
        },
        {
          id: "gl-draw-polygon-stroke-custom",
          type: "line",
          filter: ["all", ["==", "$type", "Polygon"], ["!=", "mode", "static"]],
          layout: {
            "line-cap": "round",
            "line-join": "round",
          },
          paint: {
            "line-color": "#22c55e",
            "line-width": 3,
          },
        },
        {
          id: "gl-draw-line-custom",
          type: "line",
          filter: ["all", ["==", "$type", "LineString"], ["!=", "mode", "static"]],
          layout: {
            "line-cap": "round",
            "line-join": "round",
          },
          paint: {
            "line-color": "#22c55e",
            "line-width": 3,
            "line-dasharray": [0.2, 2],
          },
        },
        {
          id: "gl-draw-point-outer-custom",
          type: "circle",
          filter: ["all", ["==", "$type", "Point"], ["!=", "meta", "midpoint"]],
          paint: {
            "circle-radius": 7,
            "circle-color": "#ffffff",
            "circle-stroke-width": 2,
            "circle-stroke-color": "#22c55e",
          },
        },
        {
          id: "gl-draw-midpoint-custom",
          type: "circle",
          filter: ["all", ["==", "meta", "midpoint"]],
          paint: {
            "circle-radius": 5,
            "circle-color": "#22c55e",
          },
        },
      ],
    });

    drawRef.current = draw;
    map.addControl(draw, "top-left");
    map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), "top-left");

    map.on("load", () => {
      addTerrainLayer(map);
      if (initialFeature) {
        draw.add(initialFeature);

        const bbox = computeBBox(initialFeature);
        if (bbox) {
          map.fitBounds(
            [
              [bbox[0], bbox[1]],
              [bbox[2], bbox[3]],
            ],
            { padding: 40, duration: 0 }
          );
        }
      }

      setReady(true);
    });

    function emitChange() {
      const fc = draw.getAll();
      const first = fc?.features?.[0] || null;
      onChange?.(first ? first.geometry : null);
    }

    map.on("draw.create", emitChange);
    map.on("draw.update", emitChange);
    map.on("draw.delete", emitChange);

    return () => {
      try {
        map.remove();
      } catch {}
      mapRef.current = null;
      drawRef.current = null;
    };
  }, [initialFeature, onChange]);

  function fitToContour() {
    const map = mapRef.current;
    const draw = drawRef.current;
    if (!map || !draw) return;

    const fc = draw.getAll();
    const first = fc?.features?.[0];
    const bbox = computeBBox(first);
    if (!bbox) return;

    map.fitBounds(
      [
        [bbox[0], bbox[1]],
        [bbox[2], bbox[3]],
      ],
      { padding: 50, duration: 500 }
    );
  }

  function startPolygon() {
    const draw = drawRef.current;
    if (!draw) return;
    draw.changeMode("draw_polygon");
  }

  function clearPolygon() {
    const draw = drawRef.current;
    if (!draw) return;
    const fc = draw.getAll();
    const ids = (fc?.features || []).map((f) => f.id).filter(Boolean);
    if (ids.length) draw.delete(ids);
    onChange?.(null);
  }

  return (
    <div className={`fd-mapEditor ${className}`}>
      <div className="fd-mapEditorToolbar">
        <button type="button" className="btn" onClick={startPolygon} disabled={!ready}>
          ✏️ Малювати контур
        </button>
        <button type="button" className="btn" onClick={fitToContour} disabled={!ready}>
          ⤢ Підігнати
        </button>
        <button type="button" className="btn" onClick={clearPolygon} disabled={!ready}>
          🗑 Очистити
        </button>
      </div>

      {error ? (
        <div className="fd-mapEditorError">{error}</div>
      ) : (
        <div
          ref={mapNodeRef}
          className="fd-mapEditorCanvas"
          style={{ height }}
        />
      )}

      <div className="fd-mapEditorHint">
        ЛКМ — ставити точки. Замкни останню точку по першій, щоб завершити полігон.
        Потім можна рухати вершини і коригувати контур точніше.
      </div>
    </div>
  );
}